/** @jsxImportSource @emotion/react */
import type { ReactNode } from 'react';
import { Fragment, memo, useMemo } from 'react';
import styled from '@emotion/styled';

type TableItem = {
  name: ReactNode;
  value: ReactNode;
};

type Props = {
  items: TableItem[];
  columnTemplate?: string;
};

export const CitationsTable = memo(({ items, columnTemplate = '125px minmax(0, 1fr)' }: Props) => {
  const rows = useMemo(() => {
    return items.map((item, i) => (
      <Fragment key={i}>
        <LabelAttribute>{item.name}</LabelAttribute>
        <Attribute>{item.value}</Attribute>
      </Fragment>
    ));
  }, [items]);

  return (
    <Container columnTemplate={columnTemplate} css={{ margin: '10px 0 0 0', width: '100%' }}>
      {rows}
    </Container>
  );
});

export const Container = styled.ol<{ columnTemplate: string }>(({ theme, columnTemplate }) => ({
  boxSizing: 'border-box',
  display: 'grid',
  gridTemplateColumns: columnTemplate,
  border: `2px solid ${theme.palette.gray.light1}`,
  borderRadius: 4,
  margin: 0,
  padding: 0,
  '& > div:nth-of-type(2n)': {
    borderRight: 0,
  },
}));

export const Attribute = styled.div(({ theme }) => ({
  boxSizing: 'border-box',
  borderRight: `1px solid ${theme.palette.gray.light2}`,
  borderBottom: `1px solid ${theme.palette.gray.light2}`,
  padding: 8,
  fontSize: 14,
  minWidth: 0,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  whiteSpace: 'normal',
  overflow: 'hidden',
  height: '100%',
}));

export const LabelAttribute = styled(Attribute)(({ theme }) => ({
  backgroundColor: theme.palette.gray.light3,
  fontFamily: theme.fonts.semiBold,
  fontSize: 14,
}));
